import { Show } from "@/components/core";
import { useImportsViewController } from "./useImportsViewController";
import { ImportDetails } from "../import-details/ImportDetails";
import { cn } from "@/lib/utils";
import { FileText, UploadIcon, X, Filter } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { ImportStatusIndicator } from "../components/ImportStatusIndicator";
import { Badge } from "@/components/ui/badge";
import { ReportingPeriodTimeline } from "@/features/reporting-periods";
import { useAtomValue } from "jotai";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const ImportsView = () => {
  const navigate = useNavigate();
  const controller = useImportsViewController();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get("type");
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  const selectedFund = useAtomValue(SelectedFundAtom);
  
  const isPeriodClosed = !selectedReportingPeriod?.isOpen;
  const isUploadDisabled = !selectedFund || isPeriodClosed;

  const getTooltipMessage = () => {
    if (!selectedFund) {
      return "Please select a fund to upload files";
    }
    if (isPeriodClosed) {
      return "Reporting period is closed. Please select or create an open period to upload files";
    }
    return "Upload new file";
  };

  return (
    <>
      <ReportingPeriodTimeline />
      <Show if={controller.loading}>
        <div className="flex items-center justify-center h-full border rounded-md bg-muted/10">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          <p className="ml-2 text-muted-foreground">Loading imports...</p>
        </div>
      </Show>
      <Show if={!controller.loading}>
        <div className="flex h-full bg-background pt-4">
          {/* Sidebar */}
          <div className="w-96 border-r border-border h-full overflow-y-auto bg-card">
            {/* Header */}
            <div className="px-6 border-b border-border bg-card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-foreground">Imports</h2>
                <Show if={!!typeFilter}>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Filter className="h-3 w-3" />
                    {typeFilter}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted"
                      onClick={controller.clearFilter}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                </Show>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                View and manage data imports
                {controller.context !== "client" && (
                  <span className="font-medium"> - {controller.context}</span>
                )}
              </p>
              
              {/* Filter indicator */}
              <Show if={!!typeFilter}>
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-border/50">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Showing imports filtered by: <span className="font-medium text-foreground">{typeFilter}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={controller.clearFilter}
                    className="ml-auto h-6 text-xs"
                  >
                    Clear filter
                  </Button>
                </div>
              </Show>
            </div>

            {/* Upload Button */}
            <div className="p-4 border-b border-border">
              {isUploadDisabled ? (
                      <div className="w-full">
                        <Button
                          onClick={() => {
                              navigate(`/app/imports/new?context=${controller.context}`);
                          }}
                          className="w-full flex items-center justify-center gap-2 h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          variant="default"
                        >
                          <UploadIcon size={18} />
                          Upload New File
                        </Button>
                      </div>
              ) : (
                <Button
                  onClick={() => {
                    navigate(`/app/imports/new?context=${controller.context}`);
                  }}
                  className="w-full flex items-center justify-center gap-2 h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                  variant="default"
                >
                  <UploadIcon size={18} />
                  Upload New File
                </Button>
              )}
            </div>

            {/* Import List */}
            <div className="py-4">
              <Show if={controller.imports.length === 0}>
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground text-sm font-medium mb-1">
                    No imports found
                  </p>
                  <p className="text-muted-foreground/70 text-xs">
                    {typeFilter ? 
                      `No imports match the filter "${typeFilter}" for ${controller.context}` :
                      `No imports found for ${controller.context}`
                    }
                  </p>
                </div>
              </Show>
              <Show if={controller.imports.length > 0}>
                <div className="divide-y divide-border">
                  {controller.imports.map((importItem, index) => (
                    <div
                      key={importItem.importId}
                      className={cn(
                        "group relative px-4 py-4 cursor-pointer transition-all duration-200",
                        "hover:bg-muted/30 border-l-4",
                        controller.selectedImport?.importId === importItem.importId
                          ? "bg-primary/5 border-l-primary"
                          : "border-l-transparent hover:border-l-border",
                        index % 2 === 0 ? "bg-background" : "bg-muted/20"
                      )}
                      onClick={() => {
                        controller.selectImport(importItem.importId);
                      }}
                    >
                      {/* Main row with key information */}
                      <div className="flex items-center gap-4 mb-2">
                        {/* Context with colored indicator */}
                        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                          <div className={cn(
                            "w-2.5 h-2.5 rounded-full flex-shrink-0",
                            importItem.context === "Transaction" ? "bg-blue-500" :
                            importItem.context === "Holding" ? "bg-green-500" :
                            importItem.context === "Account" ? "bg-purple-500" :
                            importItem.context === "Investor" ? "bg-orange-500" :
                            "bg-gray-400"
                          )} />
                          <span className="font-semibold text-sm text-foreground min-w-0">
                            {importItem.context}
                          </span>
                        </div>
                        
                        {/* File name */}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-foreground truncate block font-medium">
                            {importItem.fileName}
                          </span>
                        </div>
                      </div>
                      
                      {/* Secondary row with metadata and status */}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-6">
                          <span className="flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" />
                            <span className="font-medium">{importItem.rowCount.toLocaleString()}</span> rows
                          </span>
                          <span className="font-medium">
                            {new Date(importItem.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ImportStatusIndicator importItem={importItem} />
                          <span className="font-mono text-xs opacity-60 bg-muted/40 px-2 py-1 rounded">
                            #{String(importItem.importId).slice(-8)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Show>
            </div>
          </div>

          {/* Main Content */}
          <ImportDetails selectedImport={controller.selectedImport} />
        </div>
      </Show>
    </>
  );
};
